import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Input, List, Skeleton, Card, Flex, Row, Col, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";

import { fetchApplications } from "../../../utilities/redux/slices/applicationsSlice";

const ApplicationList = ({ onSelect, selectedApplication }) => {
  const dispatch = useDispatch();
  const scrollableDivRef = useRef(null);
  const navigate = useNavigate();

  const applications = useSelector((state) => state.applications.data);
  const total = useSelector((state) => state.applications.total);
  const pageSize = useSelector((state) => state.applications.pageSize);

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  const debouncedFetchApplications = useCallback(
    debounce((search, page) => {
      dispatch(fetchApplications({ search, page }));
    }, 200),
    [dispatch]
  );

  const loadMoreData = () => {
    if (loading) {
      return;
    }
    setLoading(true);
    const nextPage = page;
    dispatch(fetchApplications({ search: searchTerm, page: nextPage })).then(() => {
      setPage(prev => prev + 1);
      setLoading(false);
    });
  }

  useEffect(() => {
    setPage(1);
    debouncedFetchApplications(searchTerm, 1);
    setPage(2);
  }, [searchTerm]);

  useEffect(() => {
    if (applications.length === 0) {
      loadMoreData();
    }
  }, []);

  useEffect(() => {
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  return (
    <Card
      title={(
        <Input.Search
          placeholder="Search Applications"
          prefix={<SearchOutlined />}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      )}
      style={{
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      styles={{
        'body': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          minHeight: 0
        }
      }}
    >
      <div
        id="scrollableDiv"
        ref={scrollableDivRef}
        style={{
          overflowY: 'auto',
        }}
      >
        <InfiniteScroll
          dataLength={applications.length}
          next={loadMoreData}
          hasMore={applications.length < total}
          loader={<Skeleton style={{ padding: '0 16px' }} paragraph={{ rows: 1 }} active />}
          endMessage={<div style={{ textAlign: "center", padding: 16 }}>End of Applications</div>}
          scrollableTarget="scrollableDiv"
        >
          <List
            dataSource={applications}
            style={{ width: '100%' }}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                style={{
                  width: '100%',
                  background:
                    selectedApplication == item.id
                      ? '#bae7ff'
                      : hoveredId === item.id
                        ? '#e6f7ff'
                        : 'transparent',
                  transition: 'background 0.2s'
                }}
                onClick={() => {
                  onSelect(item.id);
                  navigate(`/applications/${item.id}`);
                }}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <List.Item.Meta
                  title={<Typography.Text strong style={{ fontSize: 18, margin: 0 }}>{item.name}</Typography.Text>}
                  description={
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <div>{item.application_status}</div>
                      </Col>
                      <Col span={12}>
                        <div>{new Date(item.nomination_date).toLocaleDateString()}</div>
                      </Col>
                    </Row>
                  }
                  style={{ margin: '0 16px' }}
                />
              </List.Item>
            )}
          />
        </InfiniteScroll>
      </div>
    </Card>
  );
}

export default ApplicationList;